import * as React from 'react';
import { BLAZOR_ASSEMBLY } from "@/common/constants";
import { NS } from "@/common/namespaces";
import { blazorAlert } from '@/common/utilities';
import { SubscriberService, OnUpdateEvent, createStaticServiceInstance, createUseZenAutoArtService } from "./base-subscriber-service";
import { svgEditServiceInstance } from "./svg-edit-service";
import { zenDataServiceInstance } from "./zen-data-service";
import { fontsServiceInstance } from "./fonts-service";
import { InsertElementCommand } from "@/svgcanvas/undocommands";

interface ISaveInfo {
    ID: number,
    Filename: string,
    PanelTypeID: number,
    CustomerID: number,
    CreatedByUserID: number,
    LastModifiedByUserID: number,
}

export interface IPanelReference {
    id: number;
    name: string;
}

export default class PanelsService extends SubscriberService<PanelsService> {
    public static Get(comp: React.Component, onUpdate?: OnUpdateEvent) {
        return panelsServiceInstance._RegisterComponent(comp, onUpdate);
    }

    public isLoadingPanels: boolean = false;
    public panelTypes: PanelDesigner.IPanelType[] = [];
    public panels: PanelDesigner.IPanel[] = [];
    public panelTagMappings: PanelDesigner.IMapping[] = [];

    private lastOpenedPanelID: number = -1;

    constructor() {
        super();

        setTimeout(() => {
            svgEditServiceInstance.RegisterReadyEvent(() => {
                svgEditServiceInstance.canvas.bind("cleared", () => {
                    this.SetLastOpened(-1, true);
                });
            });

            if (zenDataServiceInstance.GetDesignerMode() === "panels") {
                window.addEventListener("zaa-panelservice-panels-updated", ev => {
                    this.panels = ev.detail;
                    this._UpdateSubscribers();
                });

                window.addEventListener("zaa-panelservice-paneltypes-updated", ev => {
                    this.panelTypes = ev.detail;
                    this._UpdateSubscribers();
                });

                window.addEventListener("zaa-panelservice-tagmappings-updated", ev => {
                    this.panelTagMappings = ev.detail;
                    this._UpdateSubscribers();
                });
            }
        });
    }

    public GetPanelDetailsFromReference(panelRef: string | IPanelReference) {
        if (!panelRef) {
            return null;
        }

        if (typeof panelRef === "string") {
            panelRef = PanelsService.ParsePanelReferenceString(panelRef);
        }

        let idMatch: PanelDesigner.IPanel = null;
        let nameMatch: PanelDesigner.IPanel = null;
        for (const p of this.panels) {
            if (p.id === panelRef.id) {
                idMatch = p;
            }

            if (p.filename === panelRef.name) {
                nameMatch = p;
            }
        }

        return nameMatch ?? idMatch;
    }
    
    public SetLastOpened(panelID: number, hasChangedPanel: boolean = false) {
        this.lastOpenedPanelID = panelID;

        DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, 'SetLastOpenedPanelID', panelID);

        if (hasChangedPanel) {
            this._UpdateSubscribers();
        }
    }

    public async AddComponentPanel(componentId: number, componentFileName: string, componentPlaceholderId: string)
    {
        const compElement = document.getElementById(componentPlaceholderId).firstElementChild as SVGSVGElement;

        // Spot colour conflicts are checked by the Blazor ComponentsDialog before this is called.
        fontsServiceInstance.AddFontDependencies(compElement);

        const newComp = svgEditServiceInstance.canvas.addSVGElementFromJson({
            element: "use",
            attr: {
                "href": `#${componentPlaceholderId}`,
                "x": 0,
                "y": 0,
                "id": `svgcmp_${componentId}_${new Date().getTime()}`,
                "zaa:component-reference": `${componentId}$${componentFileName}`,
                "zaa:component-repeat-limit": 0,
                "zaa:component-repeat-columns-first": "false",
                "zaa:component-repeat-column-gap": 0,
                "zaa:component-repeat-row-gap": 0
            }
        });

        svgEditServiceInstance.canvas.undoMgr.addCommandToHistory(new InsertElementCommand(newComp, `Add component ${componentFileName}`));
        svgEditServiceInstance.canvas.selectOnly([newComp], true);
        svgEditServiceInstance.canvas.setMode("select");
    }

    public static ParsePanelReferenceString(panelIDString: string | Integer): IPanelReference {
        panelIDString = panelIDString.toString();

        let id: string = panelIDString;
        let name: string = null;
        const splitIndex = panelIDString.indexOf("$");
        if (splitIndex > 0) {
            id = panelIDString.substring(0, splitIndex);
            name = panelIDString.substring(splitIndex + 1);
        }

        return { id: parseInt(id), name };
    }

    public static GetCurrentPanelDesignerVersion(): string {
        const doc = document.getElementById("svgcontent");
        return doc?.getAttribute("zaa:version") ?? "1";
    }

}

export const panelsServiceInstance = createStaticServiceInstance(new PanelsService(), "panelsService");

// For React Functional Components
export const usePanelService = createUseZenAutoArtService(panelsServiceInstance);
